import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import { MessageAuthor } from "../Models/MessageAuthor";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import { ChatMessage } from "../Models/ChatMessage";
import { MessageGroup } from "./MessageGroup/MessageGroup";
import { MessageLoading } from "./Messages/MessageLoading";
import { sxClsx } from "utils/styleClsx";
import { ConversationInput } from "./ConversationInput";
import { useCurrentChatStore } from "../Stores/useCurrentChat";
import AgentStore from "../Stores/AgentStore";
import { EndMessage } from "./EndMessage";
import { useOpenCloseFullscreenAgent } from "../AgentUtils";
import { KBoUpdateBanner } from "../Update/KBoUpdateBanner";
import { KboUserAgreementThreadItem } from "../UserAgreement/KboUserAgreementThreadItem";

export const AgentConversation = observer(
    ({ fullscreen }: { fullscreen?: boolean }) => {
        const currentChatStore = useCurrentChatStore();
        const agentStore = useStore(AgentStore);
        const { openFullscreenAgent } = useOpenCloseFullscreenAgent();
        const scrollableChatRef = useRef<HTMLDivElement>(null);
        const chatEndRef = useRef<HTMLDivElement>(null);

        const [isAutoScrolling, setIsAutoScrolling] = useState(false);

        /**
         * This effect handles enabling/disabling auto scroll when the user scrolls
         * to/away from the bottom of the agent chat. When auto
         */
        useEffect(() => {
            if (!scrollableChatRef.current || !chatEndRef.current) return;
            const scrollRef = scrollableChatRef.current;
            const chatRef = chatEndRef.current;

            const observer = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    if (entry.isIntersecting) {
                        setIsAutoScrolling(true);
                    }
                },
                { threshold: 1 },
            );

            function onUserScroll(event: WheelEvent) {
                // deltaY > 0 indicates the user scrolled down and we can exit early,
                // this helps auto scroll stay enabled if the user
                // is scrolling down even if they're already at the very bottom.
                // Scrolling up still disables auto scroll as intended and is never
                // re-enabled till they scroll all the way down again.
                if (event.deltaY > 0) return;
                setIsAutoScrolling(false);
            }

            observer.observe(chatRef);
            scrollRef.addEventListener("wheel", onUserScroll);

            return () => {
                observer.unobserve(chatRef);
                scrollRef.removeEventListener("wheel", onUserScroll);
            };
        }, []);

        /**
         * This effect handles scrolling down when auto scrolling is active
         * and new dom elements are rendered such as suggestions or actions.
         */
        useEffect(() => {
            if (!chatEndRef.current || !scrollableChatRef.current) return;

            const observer = new MutationObserver((mutations) => {
                if (!isAutoScrolling || !chatEndRef.current) return;
                for (const mutation of mutations) {
                    if (mutation.type !== "childList") continue;

                    chatEndRef.current.scrollIntoView({ behavior: "smooth" });
                    break;
                }
            });

            observer.observe(scrollableChatRef.current, {
                childList: true,
                subtree: true,
            });

            return () => observer.disconnect();
        }, [isAutoScrolling]);

        /**
         * This effect handles scrolling to the bottom of the chat and enabling
         * auto scrolling when a user sends a message.
         */
        useEffect(() => {
            if (!chatEndRef.current || !currentChatStore) return;
            const recentMessageGroup = currentChatStore.chat.recentMessageGroup;
            if (
                recentMessageGroup &&
                recentMessageGroup.author === MessageAuthor.User
            ) {
                chatEndRef.current.scrollIntoView({ behavior: "smooth" });
                setIsAutoScrolling(true);
            }
        }, [currentChatStore?.chat, currentChatStore?.chat.recentMessageGroup, currentChatStore]);

        useEffect(() => {
            // Open research reports from their beginning; standard chats retain
            // the existing newest-message-first behavior.
            if (!scrollableChatRef.current || !chatEndRef.current) return;

            const opensWithResearchReport = currentChatStore?.chat.messageGroups[0]
                ?.messages.some((message) => message.renderMode === "htmlDocument");

            if (opensWithResearchReport) {
                scrollableChatRef.current.scrollTo({ top: 0, behavior: "auto" });
                return;
            }

            chatEndRef.current.scrollIntoView({ behavior: "auto" });
        }, [agentStore.currentChatId, currentChatStore?.chat]);

        const theme = useTheme();

        const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

        const maxMessageWidth = fullscreen
            ? "calc(max(80%, 600px))"
            : "calc(max(80%, 300px))";
        // In a new chat the agreement takes the place of the greeting.
        const hidesGreeting =
            agentStore.isKboInteractionLocked &&
            !!currentChatStore?.isUntouchedDefaultChat;

        return (
            <>
                <Grid
                    container
                    item
                    direction="column"
                    flexWrap="nowrap"
                    rowGap={3}
                    flexGrow={1}
                    paddingInline={2}
                    paddingBlock={2}
                    sx={sxClsx(
                        { overflowY: "auto", height: "100%" },
                        fullscreen && {
                            paddingX: isLargeScreen ? 20 : 4,
                            maxWidth: "100%",
                            overflowX: "hidden",
                        },
                    )}
                    ref={scrollableChatRef}
                >
                    {!fullscreen && agentStore.showKboUpdateAnnouncement && (
                        <KBoUpdateBanner />
                    )}
                    {currentChatStore?.chat.messageGroups.map((item, index) => {
                        if (hidesGreeting && index === 0) return null;

                        const isMostRecent =
                            index === currentChatStore.chat.messageGroups.length - 1;

                        const cancelledStreaming =
                            item.author === MessageAuthor.Agent &&
                            (!!item.recentMessage as unknown as ChatMessage)
                                ?.cancelled;

                        return (
                            <MessageGroup
                                fullscreen={fullscreen}
                                kboFlowPreview={currentChatStore.chat.kboFlowPreview}
                                onOpenFullscreen={openFullscreenAgent}
                                maxMessageWidth={maxMessageWidth}
                                key={`agent-chat-${index}`}
                                {...item}
                                isMostRecent={isMostRecent}
                                cancelledStreaming={cancelledStreaming}
                            />
                        );
                    })}
                    {/* The terms need the whole width of the narrow KBo panel to stay readable. */}
                    <KboUserAgreementThreadItem
                        maxWidth={fullscreen ? maxMessageWidth : "100%"}
                    />
                    <MessageLoading />
                    <EndMessage />
                    <Grid ref={chatEndRef}></Grid>
                </Grid>
                <ConversationInput fullscreen={fullscreen} />
            </>
        );
    },
);
